import { API_BASE_URL } from '../../../auth/apiClient';
/**
 * TaksasiTable Component - Grouped Version
 */

import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  useBreakpointValue,
  VStack,
  HStack,
  Flex,
  Divider,
  Skeleton,
  Stack,
  IconButton,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import previewIcon from '../../../assets/icons/preview_icon.png';
import imagepreview from '../../../assets/icons/preview_image_1.png';
import angkongIcon from '../../../assets/icons/angkong_icon.svg';
import percentIcon from '../../../assets/icons/percentage_icon.png';
import workerIcon from '../../../assets/icons/worker_icon.png';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { MdMap } from 'react-icons/md';
import TaksasiJalurMapModal from './TaksasiJalurMapModal';
// Import interface baru yang tadi kita buat
import { TaksasiCompanyGroup, TaksasiDivisionGroup, TaksasiItem, TaksasiTotal, Point } from '../types';
import { formatDateForDisplay, formatNumber, formatInteger, formatPokok, formatNumber2Decimals, formatNumber1Decimal } from '../utils/utils';

interface TaksasiTableProps {
  data: TaksasiCompanyGroup[]; // Array company groups dari API
  loading?: boolean;
}

const TaksasiCompanySummary: React.FC<{ company: TaksasiCompanyGroup }> = ({ company }) => {
  const { total } = company;
  return (
    <Box borderRadius="lg" boxShadow="sm" mb={0} overflow="hidden" bg="white">
      <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
        {/* Nama Company */}
        <Box bg="#e7efdf" p={4} flex="2.5" display="flex" alignItems="center">
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" color="gray.600" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
              Company
            </Text>
            <Text fontWeight="bold" fontSize="m">
              {company.company}
            </Text>
          </VStack>
        </Box>

        {/* Stat 1: Rata-rata AKP */}
        <Box bg="white" p={4} flex="1" borderColor="gray.100">
          <HStack spacing={3}>
            <Box
              bg="#ededed"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Image src={percentIcon} alt="AKP Icon" boxSize="25px" objectFit="contain" />
            </Box>
            <Box>
              <Text color="black"><b>Rata-rata </b>AKP</Text>
              <Text color="black"><b>{formatNumber1Decimal(total.akp)} </b>%</Text>
            </Box>
          </HStack>
        </Box>

        {/* Stat 2: Total Est. Berat */}
        <Box bg="white" p={4} flex="1" >
          <HStack spacing={3}>
            <Box
              bg="#ededed"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Image src={angkongIcon} alt="Berat Icon" boxSize="25px" objectFit="contain" />
            </Box>
            <Box>
              <Text color="black"><b>Total Est. </b>Berat</Text>
              <Text color="black"><b>{formatInteger(total.est_weight)} </b>kg</Text>
            </Box>
          </HStack>
        </Box>

        {/* Stat 3: Total Est. HK */}
        <Box bg="white" p={4} flex="1" >
          <HStack spacing={3}>
            <Box
              bg="#ededed"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Image src={workerIcon} alt="HK Icon" boxSize="25px" objectFit="contain" />
            </Box>
            <Box>
              <Text color="black"><b>Total Est. </b>HK</Text>
              <Text color="black"><b>{formatInteger(total.est_hk)}</b></Text>
            </Box>
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
};

/**
 * Mobile Card View Component (Per Item)
 */
const TaksasiMobileCard: React.FC<{ item: TaksasiItem, onPreviewImage: (url: string, title: string) => void, onPreviewMap: (points: Point[], title: string) => void }> = ({ item, onPreviewImage, onPreviewMap }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  let firstPicUrl = '';
  let firstPoints: Point[] | null = null;
  if (item.details) {
    for (const d of item.details) {
      if (d.details) {
        for (const l of d.details) {
          if (l.pic_url && !firstPicUrl) {
            firstPicUrl = l.pic_url;
          }
          if (l.points && l.points.length > 0 && !firstPoints) {
            firstPoints = l.points;
          }
          if (firstPicUrl && firstPoints) break;
        }
      }
      if (firstPicUrl && firstPoints) break;
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="flex-start">
          <VStack align="stretch" spacing={1}>
            <HStack>
              <Text fontSize="sm" fontWeight="medium" minW="80px">Kode Blok:</Text>
              <Badge colorScheme="blue" fontSize="sm">{item.block}</Badge>
            </HStack>
            <HStack>
              <Text fontSize="sm" fontWeight="medium" minW="80px">Estate:</Text>
              <Text fontSize="sm" fontWeight="medium">{item.estate}</Text>
            </HStack>
          </VStack>
          <VStack align="flex-end" spacing={1}>
            <Text fontSize="xs" color="gray.500" mb={1}>{formatDateForDisplay(item.harvest_date)}</Text>
            <HStack spacing={1}>
              {firstPicUrl && (
                <IconButton
                  aria-label="Preview"
                  icon={<Image src={imagepreview} alt="Preview" boxSize="26px" />}
                  size="md"
                  variant="ghost"
                  onClick={() => onPreviewImage(firstPicUrl, `Preview gambar`)}
                />
              )}
              {firstPoints && (
                <IconButton
                  aria-label="Map Peta Jalur"
                  icon={<MdMap size={24} />}
                  size="md"
                  variant="ghost"
                  onClick={() => onPreviewMap(firstPoints!, `Jalur Taksasi - ${item.block}`)}
                />
              )}
            </HStack>
          </VStack>
        </HStack>
        <Divider borderWidth="2px" borderColor="blue.200" />
        <VStack align="stretch" spacing={0}>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">Luas (HA):</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber2Decimals(item.planted_area)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">Plant Total:</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(item.plant_total)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">AKP (%):</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber1Decimal(item.akp)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">EST. Janjang:</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(item.est_ripe_bunch)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">BJR (KG):</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatNumber2Decimals(item.bjr)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">EST. Berat (KG):</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(item.est_weight)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">EST. HK:</Text>
            <Text fontSize="sm" fontWeight="semibold" color="black">{formatInteger(item.est_hk)}</Text>
          </HStack>
          <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
            <Text fontSize="xs" color="gray.600">Est. Output:</Text>
            <Text fontSize="sm" fontWeight="bold" color="blue.600">{formatInteger(item.est_output)}</Text>
          </HStack>
        </VStack>

        {/* Expand Button - Hanya tampil ketika belum expanded */}
        {!isExpanded && (
          <HStack justify="center">
            <IconButton
              aria-label="Expand"
              icon={<ChevronDownIcon />}
              size="md"
              variant="ghost"
              onClick={() => setIsExpanded(true)}
            />
          </HStack>
        )}

        {/* Expanded Content - Hanya tampil ketika sudah expanded */}
        {isExpanded && (
          <>
            <Divider borderWidth="2px" />

            {/* Details Section */}
            {item.details && item.details.length > 0 && (
              <VStack align="stretch" spacing={3} pt={2}>
                <Text fontSize="xs" fontWeight="bold" color="gray.700">
                  Details
                </Text>

                {/* Detail Rows */}
                {item.details.map((detail, dIdx) => (
                  <VStack align="stretch" spacing={0} key={dIdx}>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">NO. BARIS:</Text>
                      <Text fontSize="sm" fontWeight="semibold">{detail.baris_nbr}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">JML. JANJANG:</Text>
                      <Text fontSize="sm" fontWeight="semibold">{formatNumber(detail.total_bunch_count)}</Text>
                    </HStack>

                    {detail.details.map((line, lidx) => (
                      <VStack align="stretch" spacing={0} key={lidx}>
                        <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                          <Text fontSize="xs" color="gray.600">NO. POKOK:</Text>
                          <Text fontSize="xs" fontWeight="semibold" color="gray.700" whiteSpace="pre-wrap" textAlign="right">{formatPokok(line.pokok_nbrs)}</Text>
                        </HStack>
                        <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                          <Text fontSize="xs" color="gray.600">MATI / SISIP:</Text>
                          <Text fontSize="sm" fontWeight="semibold">{formatNumber(line.is_dead_or_sisip)}</Text>
                        </HStack>
                        <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                          <Text fontSize="xs" color="gray.600">JML. POKOK:</Text>
                          <Text fontSize="sm" fontWeight="semibold">{formatNumber(line.pokok_total)}</Text>
                        </HStack>
                        <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                          <Text fontSize="xs" color="gray.600">POKOK PRODUKTIF:</Text>
                          <Text fontSize="sm" fontWeight="semibold">{formatNumber(line.productive_pokok)}</Text>
                        </HStack>
                      </VStack>
                    ))}
                  </VStack>
                ))}
              </VStack>
            )}

            {/* Total Details Section */}
            {item.details_total && (
              <>
                <Divider borderWidth="2px" />
                <VStack align="stretch" spacing={3} pt={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                    Total Details
                  </Text>

                  <VStack align="stretch" spacing={0}>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">TOTAL JML. JANJANG:</Text>
                      <Text fontSize="sm" fontWeight="semibold">{formatNumber(item.details_total.total_bunch_count_total)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">TOTAL MATI / SISIP:</Text>
                      <Text fontSize="sm" fontWeight="semibold">{formatNumber(item.details_total.is_dead_or_sisip_total)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">TOTAL JML. POKOK:</Text>
                      <Text fontSize="sm" fontWeight="semibold">{formatNumber(item.details_total.total_pokok_total)}</Text>
                    </HStack>
                    <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                      <Text fontSize="xs" color="gray.600">TOTAL POKOK PRODUKTIF:</Text>
                      <Text fontSize="sm" fontWeight="semibold" color="blue.700">{formatNumber(item.details_total.productive_pokok_total)}</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </>
            )}

            {/* Collapse Button - Di paling bawah */}
            <HStack justify="center" pt={2}>
              <IconButton
                aria-label="Collapse"
                icon={<ChevronUpIcon />}
                size="md"
                variant="ghost"
                onClick={() => setIsExpanded(false)}
              />
            </HStack>
          </>
        )}
      </VStack>
    </Box>
  );
};

/**
 * Mobile Summary Card (Per Divisi)
 * bg="white"
      p={4}
      borderRadius="lg"
      boxShadow="sm"
      border="1px"
      borderColor="gray.200"
 */
const TaksasiMobileTotal: React.FC<{ total: TaksasiTotal; division: string }> = ({ total, division }) => (
  <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
    <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">SUMMARY</Text>
    <VStack align="stretch" spacing={0}>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Luas:</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatNumber2Decimals(total.planted_area)} Ha</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Plant:</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total.plant_total)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total AKP (%):</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatNumber1Decimal(total.akp)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Janjang:</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total.est_ripe_bunch)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total BJR (kg):</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatNumber2Decimals(total.bjr)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Berat (kg):</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total.est_weight)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" borderBottom="1px solid" borderColor="blackAlpha.100" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Est. HK:</Text>
        <Text fontSize="sm" fontWeight="bold" color="black">{formatInteger(total.est_hk)}</Text>
      </HStack>
      <HStack justify="space-between" w="full" py={2} px={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" color="black">Total Est. Output:</Text>
        <Text fontSize="sm" fontWeight="bold" color="blue.700">{formatInteger(total.est_output)}</Text>
      </HStack>
    </VStack>
  </Box>
);

const TaksasiTable: React.FC<TaksasiTableProps> = ({ data, loading = false }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isMapOpen, onOpen: onMapOpen, onClose: onMapClose } = useDisclosure();
  const [mapData, setMapData] = useState<{ points: Point[]; title: string } | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [previewImageTitle, setPreviewImageTitle] = useState<string>('');

  const handlePreviewImage = (imagePath: string | undefined, title: string) => {
    if (!imagePath) return;
    const fullUrl = imagePath.startsWith('http') ? imagePath : `${API_BASE_URL}${imagePath}`;
    setPreviewImageUrl(fullUrl);
    setPreviewImageTitle(title);
    onOpen();
  };

  const handlePreviewMap = (points: Point[], title: string) => {
    setMapData({ points, title });
    onMapOpen();
  };

  const toggleRow = (rowKey: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  const toggleGroup = (groupId: string | number) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  if (loading) {
    return (
      <Box>
        {isMobile ? (
          <VStack spacing={3}>
            {[1, 2, 3].map((i) => <Skeleton key={i} height="150px" borderRadius="lg" width="100%" />)}
          </VStack>
        ) : (
          <Stack spacing={2}>
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="50px" borderRadius="md" width="100%" />)}
          </Stack>
        )}
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box bg="white" p={8} borderRadius="xl" textAlign="center" border="1px" borderColor="gray.200">
        <Text color="gray.500">Tidak ada data Taksasi</Text>
      </Box>
    );
  }
  const isAnyGroupExpanded = Object.values(expandedGroups).some(Boolean);

  return (
    <>
      {isMobile ? (
        <VStack spacing={6} align="stretch">
          {data.map((company) => (
            <VStack key={company.company_id} align="stretch" spacing={3}>
              {/* Company Summary Header - Mobile */}
              <TaksasiCompanySummary company={company} />
              {company.estates.map((estate) => (
                <VStack key={estate.estate_id} align="stretch" spacing={3}>
                  {estate.divisions.map((group) => (
                    <VStack key={group.division_id} align="stretch" spacing={3}>
                      <HStack>
                        <Text fontWeight="bold" color="blue.700" fontSize="md" pl={1}>
                          {group.division} ({estate.estate})
                        </Text>
                        <IconButton
                          aria-label="Expand division"
                          icon={expandedGroups[group.division_id] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleGroup(group.division_id)}
                        />
                      </HStack>
                      <TaksasiMobileTotal total={group.total} division={group.division} />
                      {expandedGroups[group.division_id] && (
                        <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" overflow="hidden">
                          <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.200" />}>
                            {group.items.map((item) => (
                              <Box p={4} key={item.id}>
                                <TaksasiMobileCard item={item} onPreviewImage={handlePreviewImage} onPreviewMap={handlePreviewMap} />
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  ))}
                </VStack>
              ))}
            </VStack>
          ))}
        </VStack>
      ) : (
        <VStack spacing={6} align="stretch">
          {data.map((company) => (
            <React.Fragment key={company.company_id}>
              {/* Company Summary Header - Desktop */}
              <TaksasiCompanySummary company={company} />
              {company.estates.map((estate) => {
                const isAnyGroupInThisEstateExpanded = estate.divisions.some(division => expandedGroups[division.division_id]);
                return (
                  <Box key={estate.estate_id} bg="white" borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.200" overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.200">
                        <Tr bg="#4A5A33">
                          {isAnyGroupInThisEstateExpanded ? (
                            <>
                              <Th color="white" py={2}>Tanggal</Th>
                              <Th color="white" py={2}>Blok</Th>
                            </>
                          ) : (
                            <Th py={2}></Th>
                          )}
                          <Th isNumeric color="white" py={2}>Luas (Ha)</Th>
                          <Th isNumeric color="white" py={2}>Plant Total</Th>

                          <Th isNumeric color="white" py={2}>AKP (%)</Th>
                          <Th isNumeric color="white" py={2}>Est. Janjang</Th>
                          <Th isNumeric color="white" py={2}>BJR (kg)</Th>
                          <Th isNumeric color="white" py={2}>Est. Berat (kg)</Th>
                          <Th isNumeric color="white" py={2}>Est. HK</Th>
                          <Th isNumeric color="white" py={2}>Est. Output</Th>
                        </Tr>
                        {/* Estate Total Row */}
                        <Tr bg="#e7efdf">
                          <Td colSpan={isAnyGroupInThisEstateExpanded ? 2 : 1} py={2} pr={4}>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" color="gray.800" fontSize="xs">
                                {estate.estate}
                              </Text>
                              <Text fontWeight="bold" color="gray.600" fontSize="xs">
                                TOTAL
                              </Text>
                            </VStack>
                          </Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber2Decimals(estate.total.planted_area)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.total.plant_total)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber1Decimal(estate.total.akp)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.total.est_ripe_bunch)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber2Decimals(estate.total.bjr)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.total.est_weight)}</Td>
                          <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(estate.total.est_hk)}</Td>
                          <Td fontSize="xs" isNumeric color="blue.700" fontWeight="bold">{formatInteger(estate.total.est_output)}</Td>
                        </Tr>
                      </Thead>
                      <Tbody>

                        {estate.divisions.map((group) => (
                          <React.Fragment key={group.division_id}>
                            {/* Header Grup Divisi with Totals */}
                            <Tr _hover={{ bg: "blue.50" }}>
                              <Td colSpan={isAnyGroupInThisEstateExpanded ? 2 : 1} py={2} pr={4}>
                                <HStack spacing={2}>
                                  <Text fontWeight="bold" color="blue.800" fontSize="xs">
                                    {group.division}
                                  </Text>
                                  <IconButton aria-label='Expand group'
                                    icon={expandedGroups[group.division_id] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => toggleGroup(group.division_id)}
                                  />
                                </HStack>
                              </Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber2Decimals(group.total.planted_area)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.total.plant_total)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber1Decimal(group.total.akp)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.total.est_ripe_bunch)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatNumber2Decimals(group.total.bjr)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.total.est_weight)}</Td>
                              <Td fontSize="xs" isNumeric fontWeight="bold">{formatInteger(group.total.est_hk)}</Td>
                              <Td fontSize="xs" isNumeric color="blue.700" fontWeight="bold">{formatInteger(group.total.est_output)}</Td>
                            </Tr>

                            {/* Data Items */}
                            {expandedGroups[group.division_id] && group.items.map((item) => {
                              const rowKey = item.id;
                              const isExpanded = expandedRows[rowKey] || false;

                              return (
                                <React.Fragment key={item.id}>
                                  <Tr _hover={{ bg: 'blue.50' }}>
                                    <Td fontSize="xs">
                                      <HStack spacing={2}>
                                        {item.details && item.details.length > 0 && (
                                          <IconButton aria-label='Expand row'
                                            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => toggleRow(rowKey)}
                                          />
                                        )}
                                        <Text>{formatDateForDisplay(item.harvest_date)}</Text>
                                      </HStack>
                                    </Td>
                                    <Td fontSize="xs" >{item.block}</Td>
                                    <Td fontSize="xs" isNumeric>{formatNumber2Decimals(item.planted_area)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatInteger(item.plant_total)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatNumber1Decimal(item.akp)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatInteger(item.est_ripe_bunch)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatNumber2Decimals(item.bjr)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatInteger(item.est_weight)}</Td>
                                    <Td fontSize="xs" isNumeric>{formatInteger(item.est_hk)}</Td>
                                    <Td fontSize="xs" isNumeric color="blue.600">
                                      {formatInteger(item.est_output)}
                                    </Td>
                                  </Tr>

                                  {isExpanded && item.details && item.details.length > 0 && (
                                    <>
                                      <Tr>
                                        <Td></Td>
                                        <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="left">MANDOR</Text></Td>
                                        <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">NO. BARIS</Text></Td>
                                        <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600" textAlign="right">JML. JANJANG</Text></Td>
                                        <Td><Text fontSize="2xs" fontWeight="bold" color="gray.600">NO. POKOK</Text></Td>
                                        <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600">MATI / SISIP</Text></Td>
                                        <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600">JML. POKOK</Text></Td>
                                        <Td isNumeric><Text fontSize="2xs" fontWeight="bold" color="gray.600">POKOK PRODUKTIF</Text></Td>
                                        <Td colSpan={2}></Td>
                                      </Tr>

                                      {item.details.map((detail, dIdx) => (
                                        detail.details.map((line, lIdx) => (
                                          <Tr key={`${dIdx}-${lIdx}`} bg="white" _hover={{ bg: 'gray.50' }}>
                                            <Td></Td>
                                            <Td><Text fontSize="xs">{detail.create_by}</Text></Td>
                                            <Td isNumeric>
                                              <HStack justify="flex-end" spacing={1}>
                                                <Text fontSize="xs" textAlign="right">{detail.baris_nbr}</Text>
                                                {line.pic_url && (
                                                  <IconButton
                                                    aria-label="Preview"
                                                    icon={<Image src={previewIcon} alt="Preview" boxSize="16px" />}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handlePreviewImage(line.pic_url, `Preview gambar`)}
                                                  />
                                                )}
                                              </HStack>
                                            </Td>
                                            <Td isNumeric><Text fontSize="xs" textAlign="right">{lIdx === 0 ? formatNumber(detail.total_bunch_count) : ''}</Text></Td>
                                            <Td>
                                              <HStack spacing={1}>
                                                <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">{formatPokok(line.pokok_nbrs)}</Text>
                                                {line.points && line.points.length > 0 && (
                                                  <IconButton
                                                    aria-label="Map Peta Jalur"
                                                    icon={<MdMap size={18} />}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handlePreviewMap(line.points!, `Peta Jalur - Baris ${detail.baris_nbr}`)}
                                                  />
                                                )}
                                              </HStack>
                                            </Td>
                                            <Td isNumeric><Text fontSize="xs">{formatNumber(line.is_dead_or_sisip)}</Text></Td>
                                            <Td isNumeric><Text fontSize="xs">{formatNumber(line.pokok_total)}</Text></Td>
                                            <Td isNumeric><Text fontSize="xs">{formatNumber(line.productive_pokok)}</Text></Td>
                                            <Td colSpan={2}></Td>
                                          </Tr>
                                        ))
                                      ))}

                                      {item.details_total && (
                                        <Tr borderTop="1px solid" borderColor="blackAlpha.100">
                                          <Td colSpan={3}><Text fontSize="xs" fontWeight="bold" textAlign="right">TOTAL</Text></Td>
                                          <Td isNumeric><Text fontSize="xs" fontWeight="bold">{formatNumber(item.details_total.total_bunch_count_total)}</Text></Td>
                                          <Td></Td>
                                          <Td isNumeric><Text fontSize="xs" fontWeight="bold">{formatNumber(item.details_total.is_dead_or_sisip_total)}</Text></Td>
                                          <Td isNumeric><Text fontSize="xs" fontWeight="bold">{formatNumber(item.details_total.total_pokok_total)}</Text></Td>
                                          <Td isNumeric><Text fontSize="xs" fontWeight="bold">{formatNumber(item.details_total.productive_pokok_total)}</Text></Td>
                                          <Td colSpan={2}></Td>
                                        </Tr>
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}

                          </React.Fragment>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </VStack>
      )}

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="lg" overflow="hidden" maxW="90vw" w="fit-content" mx={4}>
          <ModalHeader bg="customGold.500" color="white" pr={12} fontSize="xl">
            {previewImageTitle}
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody display="flex" justifyContent="center" alignItems="center" p={6}>
            {previewImageUrl ? (
              <Image
                src={previewImageUrl}
                alt={previewImageTitle}
                maxW="100%"
                maxH="70vh"
                objectFit="contain"
                borderRadius="lg"
              />
            ) : (
              <Text>Gambar tidak tersedia</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Map Preview Modal */}
      <TaksasiJalurMapModal isOpen={isMapOpen} onClose={onMapClose} jalurData={mapData} />
    </>
  );
};

export default TaksasiTable;